import { Injectable } from '@angular/core';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { BANNER_TYPES } from '@modules/rx-intuitive/models/banner-type';
import { FLOW_RX_STEPS, FlowRxStep } from '@modules/rx-intuitive/models/flow-rx-step';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { BehaviorSubject, combineLatest, EMPTY, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ResendScanService } from '@modules/rx-for-doctor/services/resend-scan.service';
import { SaveRxService } from '@modules/rx-for-doctor/services/save-rx.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { EventPublisherService } from '@bootstrap/services/event-publisher.service';
import moment from 'moment';
import { BiObjectName } from '@core/services/logger/interfaces/bi.enums';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';

@Injectable()
export class RxIntuitiveForDoctorFacade {
	readonly isSummaryMode$ = this.intuitiveFlowService.isSummaryMode$;
	readonly flowRxStep$ = this.shellQuery.flowRxStep$;
	readonly isResendScan$ = this.shellQuery.isResendScan$;
	readonly isRxValidForSave$ = this.shellQuery.isRxValidForSave$;
	readonly isFallbackBannerAvailable$ = this.intuitiveFlowService.isFallbackBannerAvailable$;

	readonly isContinueToLabBrokerButtonVisible$: Observable<boolean> = this.rxTemplateService.template$.pipe(
		map(template => template.ui.summaryFooter.continueToLabBrokerButton.hidden !== true)
	);

	readonly continueToLabBrokerName$: Observable<string | null> = this.orderQuery.sendTo$.pipe(
		map(sendTo => (sendTo && 'Settings' in sendTo ? sendTo.Settings?.customRxIntegration.ui.continueToNameText : undefined) ?? null)
	);

	readonly labBrokerSendStepBypassed$ = this.shellQuery.labBrokerSendStepBypassed$;
	private readonly facadeName = 'RxIntuitiveForDoctorFacade';

	private readonly resendScanError$ = new BehaviorSubject<boolean>(false);
	readonly hasResendScanError$ = this.resendScanError$.asObservable();

	private readonly resendScanPending$ = new BehaviorSubject<boolean>(false);
	readonly isResendScanPending$ = this.resendScanPending$.asObservable();

	readonly isSendToMissing$: Observable<boolean> = combineLatest([
		this.shellQuery.isResendScan$,
		this.orderQuery.sendTo$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(
			([isResendScan, sendTo, procedureMap]) =>
				isResendScan && !sendTo && this.procedureFormStateService.isSendToRequiredForResend(procedureMap)
		)
	);

	readonly isDueDateExpired$: Observable<boolean> = combineLatest([
		this.orderQuery.dueDate$,
		this.orderQuery.minimalAvailableDueDate$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(([dueDate, minDate, procedureMap]) => {
			if (!this.procedureFormStateService.isDueDateRelevant(procedureMap) || !dueDate || !minDate) {
				return false;
			}

			return moment.utc(dueDate).isBefore(moment.utc(minDate), 'day');
		})
	);

	readonly bannerType$ = combineLatest([
		this.shellQuery.isResendScan$,
		this.shellQuery.isReadOnly$,
		this.shellQuery.isRxSent$,
		this.hostPlatformService.isScanner$,
		this.orderQuery.isDraftSelected$
	]).pipe(
		map(([isResendScan, isReadOnly, isRxSent, isScanner, isDraftSelected]) => {
			if (isResendScan) {
				return BANNER_TYPES.Resend;
			}

			const shouldShowReadOnly = isScanner ? isReadOnly : isReadOnly && isRxSent && !isDraftSelected;

			return shouldShowReadOnly ? BANNER_TYPES.ReadOnly : null;
		})
	);

	getInitialStep(): Observable<FlowRxStep> {
		return this.shellQuery.rxConfiguration$.pipe(
			filter(x => !!x),
			take(1),
			switchMap(() => {
				if (this.shellQuery.rxId || this.shellQuery.orderId) {
					return this.shellQuery.rx$.pipe(
						filter(rx => !!rx),
						take(1),
						map(() => FLOW_RX_STEPS.RxForm)
					);
				}

				if (this.shellQuery.clonedFromRxID) {
					return this.shellQuery.clonedRx$.pipe(
						filter(rx => !!rx),
						take(1),
						map(() => (this.shellQuery.rx ? FLOW_RX_STEPS.RxForm : FLOW_RX_STEPS.ProcedureSelection))
					);
				}

				if (this.shellQuery.patientGuidProvided) {
					// Patient may have been explicitly cleared by user in old mode
					if (this.patientQuery.isPatientCleared) {
						return of(FLOW_RX_STEPS.PatientDetails);
					}

					return this.patientQuery.patient$.pipe(
						filter(patient => !!patient),
						take(1),
						map(() => FLOW_RX_STEPS.ProcedureSelection)
					);
				}

				if (this.patientQuery.patient) {
					return of(FLOW_RX_STEPS.ProcedureSelection);
				}

				return of(FLOW_RX_STEPS.PatientDetails);
			})
		);
	}

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly shellStore: ShellStore,
		private readonly orderQuery: OrderQuery,
		private readonly patientQuery: PatientQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly intuitiveFlowService: IntuitiveFlowService,
		private readonly resendScanService: ResendScanService,
		private readonly saveRxService: SaveRxService,
		private readonly logger: LoggerService,
		private readonly biLoggerService: BiLoggerService,
		private readonly publisherService: EventPublisherService,
		private readonly rxTemplateService: RxTemplateService,
		private readonly procedureFormStateService: ProcedureFormStateService
	) {}

	updateFlowRxStep(flowRxStep: FlowRxStep): void {
		this.shellStore.update({ flowRxStep });
	}

	resetFlowRxStep(): void {
		this.shellStore.update({ flowRxStep: undefined });
	}

	bypassLabBrokerSendStep(): void {
		this.shellStore.update({ labBrokerSendStepBypassed: true });
	}

	dismissResendScanError(): void {
		this.resendScanError$.next(false);
	}

	resendScan(): Observable<void> {
		if (this.shellQuery.isRxValidForSave !== true) {
			this.logger.info('RXLog - Resend Scan blocked: Rx is not valid for save.', {
				module: this.facadeName,
				extendedParameters: { rxId: this.shellQuery.rx?.ID ?? '' }
			});

			return EMPTY;
		}

		this.resendScanStarted();

		const rx = this.saveRxService.prepareRxModelToSave(this.shellQuery.rx);
		const originalRxId = rx.ClonedFromRxID ?? '';
		const clonedRxId = rx.ID ?? '';
		const originalOrderId = rx.Order.ID ?? 0;

		return this.resendScanService.resendScan(rx.Order.ID ?? 0, rx).pipe(
			tap(response => {
				if (response == null) {
					this.reportResendScanError('Response was null', originalRxId, originalOrderId);
					this.resendScanFailed();
				} else {
					this.reportResendScanSuccess(originalRxId, originalOrderId, clonedRxId, response.Result.ClonedOrderId);
					this.resendScanSucceeded();
				}
			}),
			catchError(error => {
				this.logger.error('RXLog - Error Resending Scan.', { module: this.facadeName, extendedParameters: error });
				this.reportResendScanError(error?.message ?? 'Unknown error', originalRxId, originalOrderId);
				this.resendScanFailed();

				return of(error);
			}),
			map(() => void 0)
		);
	}

	private reportResendScanSuccess(originalRxId: string, originalOrderId: number, clonedRxId: string, clonedOrderId: number): void {
		void this.biLoggerService.reporter?.trackStage({
			object: BiObjectName.SendCopy,
			stage: 'successful',
			description: 'Send Copy successful',
			previous: '',
			trigger: 'user',
			customAttributes: {
				originalRxId,
				clonedRxId,
				originalOrderId,
				clonedOrderId
			}
		});
	}

	private reportResendScanError(errorDescription: string, originalRxId: string, originalOrderId: number): void {
		void this.biLoggerService.reporter?.trackError({
			code: 'failed',
			object: BiObjectName.SendCopy,
			severity: 'warning',
			description: errorDescription,
			customAttributes: {
				originalRxId,
				originalOrderId
			}
		});
	}

	private resendScanStarted(): void {
		this.resendScanError$.next(false);
		this.resendScanPending$.next(true);
	}

	private resendScanSucceeded(): void {
		this.resendScanPending$.next(false);
		void this.publisherService.publishCloseMe();
	}

	private resendScanFailed(): void {
		this.resendScanError$.next(true);
		this.resendScanPending$.next(false);
	}
}
